import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn.js';
import { controlClasses, describedBy, useField } from './field.js';
/**
 * Where a filter box starts earning its keystroke.
 *
 * Below this a list is faster to read than to type into, and a search input
 * above three options is furniture. A country list is unusable without one.
 * Callers that know better set `searchable` explicitly.
 */
const SEARCH_THRESHOLD = 8;
const END_REACHED_THRESHOLD_PX = 20;
export function FieldSelect({ value, onValueChange, options, placeholder = 'Select…', disabled = false, className, onBlur, searchable, onSearchChange, onEndReached, hasNextPage, }) {
    const field = useField();
    const listId = useId();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const activeRef = useRef(null);
    const updateQuery = (next) => {
        setQuery(next);
        onSearchChange?.(next);
    };
    const showSearch = searchable ?? options.length > SEARCH_THRESHOLD;
    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        if (needle === '')
            return options;
        // Substring rather than prefix: someone looking for "United Kingdom" may
        // well type "kingdom", and a prefix match would show them nothing.
        // Some dropdowns are keyed by IDs or codes, so the value is searchable too.
        return options.filter((option) => option.label.toLowerCase().includes(needle) || option.value.toLowerCase().includes(needle));
    }, [options, query]);
    const selected = options.find((option) => option.value === value);
    // Keyboard navigation has to scroll, or the highlight moves out of sight and
    // the arrow keys appear to stop working.
    useEffect(() => {
        activeRef.current?.scrollIntoView({ block: 'nearest' });
    }, [activeIndex]);
    const commit = (option) => {
        onValueChange(option.value);
        setOpen(false);
    };
    const onKeyDown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (matches.length === 0)
                return;
            const step = event.key === 'ArrowDown' ? 1 : -1;
            // Wraps, so holding a key does not dead-end at either edge.
            setActiveIndex((current) => (current + step + matches.length) % matches.length);
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            const option = matches[activeIndex];
            if (option !== undefined)
                commit(option);
        }
    };
    return (_jsxs(PopoverPrimitive.Root, { open: open, onOpenChange: (next) => {
            setOpen(next);
            if (next) {
                // Reopening should not inherit the last search, and the highlight
                // belongs on whatever is currently chosen rather than the first row.
                // Done here rather than in an effect: an effect that sets state on
                // open costs a second render to show the same thing.
                updateQuery('');
                const index = options.findIndex((option) => option.value === value);
                setActiveIndex(index === -1 ? 0 : index);
            }
            else {
                onBlur?.();
            }
        }, children: [_jsxs(PopoverPrimitive.Trigger, { id: field.controlId, 
                // The combobox role is what tells a screen reader this opens a list,
                // and which list, rather than announcing a button with a country name.
                role: "combobox", "aria-expanded": open, "aria-controls": listId, "aria-invalid": field.hasError || undefined, "aria-required": field.required || undefined, "aria-describedby": describedBy(field), disabled: disabled, className: cn(controlClasses, 'h-10 items-center justify-between text-left', className), children: [_jsx("span", { className: cn('truncate', selected === undefined && 'text-muted-foreground'), children: selected?.label ?? placeholder }), _jsx(ChevronDown, { className: "h-4 w-4 shrink-0 opacity-60", "aria-hidden": "true" })] }), _jsx(PopoverPrimitive.Portal, { children: _jsxs(PopoverPrimitive.Content, { align: "start", sideOffset: 4, className: "z-dropdown w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-md border border-border bg-background shadow-overlay", onKeyDown: onKeyDown, children: [!showSearch ? null : (_jsxs("div", { className: "flex items-center gap-2 border-b border-border px-3", children: [_jsx(Search, { className: "h-4 w-4 shrink-0 opacity-60", "aria-hidden": "true" }), _jsx("input", { 
                                    // A real input rather than a keystroke buffer, so the text is
                                    // visible, correctable, and clearable — the three things
                                    // typeahead cannot do. Radix moves focus here on open, so no
                                    // autoFocus is needed and none is used.
                                    value: query, onChange: (event) => {
                                        updateQuery(event.target.value);
                                        setActiveIndex(0);
                                    }, placeholder: "Search\u2026", "aria-label": "Filter options", "aria-controls": listId, className: "h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" })] })), _jsx("div", { id: listId, role: "listbox", className: "max-h-60 overflow-y-auto p-1", onScroll: (event) => {
                                if (!onEndReached || hasNextPage === false)
                                    return;
                                const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
                                if (scrollTop + clientHeight >= scrollHeight - END_REACHED_THRESHOLD_PX)
                                    onEndReached();
                            }, children: matches.length === 0 ? (_jsxs("p", { className: "px-2 py-6 text-center text-sm text-muted-foreground", children: ["No matches for \u201C", query, "\u201D"] })) : (matches.map((option, index) => {
                                const isActive = index === activeIndex;
                                const isSelected = option.value === value;
                                return (_jsxs("button", { ref: isActive ? activeRef : null, type: "button", role: "option", "aria-selected": isSelected, 
                                    // Focus stays in the filter input so typing keeps working;
                                    // the arrow keys move this highlight instead.
                                    tabIndex: -1, onClick: () => {
                                        commit(option);
                                    }, onMouseEnter: () => {
                                        setActiveIndex(index);
                                    }, className: cn('relative flex w-full select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-left text-sm', isActive && 'bg-accent text-accent-foreground'), children: [_jsx("span", { className: "truncate", children: option.label }), isSelected ? (_jsx(Check, { className: "absolute right-2 h-4 w-4", "aria-hidden": "true" })) : null] }, option.value));
                            })) })] }) })] }));
}
//# sourceMappingURL=field-select.js.map